import React, { FC, useCallback, useState, Suspense, useRef, useEffect } from 'react';
import { NetworkStatus } from '@apollo/client';
import { Box, Spinner, SpinnerSize, Text, Heading, theme } from '@bechtel/gds';
import { AuthRole } from '@bechtel/types';
import { get, filter, map, findIndex } from 'lodash';
import { RoleItem } from './item';
import { SuspenseLoader, ErrorRetry, ErrorBoundary } from '@bechtel/client-common';
import { useRoleQuery } from '~/user-admin/hooks/use-role';
import { DeleteRole } from '~/user-admin/roles/list/delete-role';
import { t, Trans } from '@lingui/macro';

export interface RoleListProps {
  scrollToBottom: () => void;
}
export const RoleList: FC<RoleListProps> = ({ scrollToBottom }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const addedRoleIdRef = useRef<string>('');
  const [selectedRole, setSelectedRole] = useState<AuthRole | null>(null);
  const { loading, error, data: rolesData, refetch, networkStatus } = useRoleQuery(true);

  useEffect(() => {
    if (!addedRoleIdRef.current || addedRoleIdRef.current == '') return;
    scrollToBottom();
  }, [addedRoleIdRef.current]);

  const handleDelete = useCallback((role: AuthRole) => {
    setSelectedRole(role);
    setShowDeleteConfirm(true);
  }, []);

  if (networkStatus === NetworkStatus.refetch || loading) {
    return (
      <Box
        p={{ sm: '500' }}
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'center' }}
        flexGrow={{ sm: 1 }}
        css={'height: 100%;'}
        data-testid="loading"
      >
        <Spinner size={SpinnerSize.LARGE} />
      </Box>
    );
  }
  if (!loading && error) {
    return (
      <Box data-testid="error-container">
        <ErrorRetry refetch={refetch}>
          <Trans>There was error loading role list. Please click the button below to try again.</Trans>
        </ErrorRetry>
      </Box>
    );
  }
  const roleItems: AuthRole[] = get(rolesData, 'roles', []);
  const defaultRoles = filter(roleItems, (item) => !item.sourceRole);
  const customRoles = filter(roleItems, (item) => !!item.sourceRole);

  const getRoleNameForCopy = (name: AuthRole['name']): string => {
    const copyRoleName = t`Copy of ` + name;
    if (!roleItems || roleItems.length == 0) return copyRoleName;

    if (findIndex(roleItems, (roleItem) => roleItem.name.toLowerCase() == copyRoleName.toLowerCase()) == -1)
      return copyRoleName;

    let i = 1;
    let copyRoleNameIndexed = '';
    do {
      copyRoleNameIndexed = `${copyRoleName}(${i})`;
      if (findIndex(roleItems, (roleItem) => roleItem.name.toLowerCase() == copyRoleNameIndexed.toLowerCase()) == -1)
        break;
      i++;
    } while (i > 0);
    return copyRoleNameIndexed;
  };

  if (!roleItems?.length && !loading && !error) {
    return (
      <Box>
        <Text>
          <Trans>No Roles Found</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box flexDirection={{ sm: 'column' }} px={{ sm: '700' }} py={{ sm: '700' }}>
        <Heading weight="600" size="500" data-testid="default-role-header">
          <Trans>Default Roles</Trans>
        </Heading>
        <Text
          color="grey.1000"
          family="graphik"
          size="300"
          style={{ marginTop: `${theme.spacings[450]}`, marginBottom: `${theme.spacings[450]}` }}
          data-testid="default-role-header-desc"
        >
          <Trans>Copy one of these roles to create new, custom role. You cannot rename or delete a default role.</Trans>
        </Text>
        {map(defaultRoles, (defaultRole) => (
          <RoleItem
            item={defaultRole}
            key={defaultRole.id}
            onDelete={handleDelete}
            addedRoleIdRef={addedRoleIdRef}
            isAddedRole={false}
            getRoleNameForCopy={getRoleNameForCopy}
          ></RoleItem>
        ))}
      </Box>
      {customRoles && customRoles.length > 0 && (
        <Box flexDirection={{ sm: 'column' }} px={{ sm: '700' }} py={{ sm: '700' }}>
          <Heading weight="600" size="500" data-testid="custom-role-header">
            <Trans>Custom Roles</Trans>
          </Heading>
          <Text
            color="grey.1000"
            family="graphik"
            size="300"
            style={{ marginTop: `${theme.spacings[450]}`, marginBottom: `${theme.spacings[450]}` }}
            data-testid="custom-role-header-desc"
          >
            <Trans>Copy, rename or delete your custom project roles.</Trans>
          </Text>
          {map(customRoles, (customRole) => (
            <RoleItem
              item={customRole}
              key={customRole.id}
              onDelete={handleDelete}
              addedRoleIdRef={addedRoleIdRef}
              isAddedRole={addedRoleIdRef.current == customRole.id}
              getRoleNameForCopy={getRoleNameForCopy}
            ></RoleItem>
          ))}
        </Box>
      )}
      {showDeleteConfirm && selectedRole && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <DeleteRole
              role={selectedRole}
              onCancel={() => {
                setShowDeleteConfirm(false);
                setSelectedRole(null);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
